import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip as BootstrapTooltip,
} from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import { Tooltip as MuiTooltip } from "@mui/material";

import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const SubAccountMaster = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const [subAccounts, setSubAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim() || "");

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // MUST match FastAPI param names (with leading _)
  const defaultSearchPayload = {
    _account_master_id: null,
    _party_code: "",
    _party_name: "",
    _active: null,
    _party_type_id: null,
    _party_nature_id: null,
    _party_gst_type_id: null,
    _party_state_id: null,
    _party_gst_registration_no: "",
    _party_provisional_registration_no: "",
    _aadhar_number: "",
    _pan_number: "",
  };

  const mapSubAccountRow = (r) => ({
    sub_account_id: r.sub_account_master_id ?? r.sub_account_id,
    account_code: r.account_name ?? r.account_code ?? "",
    party_code: r.party_code ?? "",
    party_name: r.party_name ?? "",
    opening_balance: r.opening_balance,
    party_type: r.party_type_name ?? r.party_type ?? "",
    party_nature: r.party_nature_name ?? r.party_nature ?? "",
    pan: r.pan_number ?? r.pan ?? "",
    aadhar_no: r.aadhar_number ?? r.aadhar_no ?? "",
    is_active: r.activestatus ?? r.is_active ?? "",
    codedisabled: r.codedisabled,
  });

  const fetchSubAccounts = async (filtersPayload = {}) => {
    try {
      const payload = {
        ...defaultSearchPayload,
        ...filtersPayload,
      };

      const response = await axios.get(baseUrl + "/searchsubaccount/", {
        params: payload,
        headers: { "Content-Type": "application/json" },
      });

      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = data.map(mapSubAccountRow);
      setSubAccounts(mapped);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No data found – show empty grid
        setSubAccounts([]);
        return;
      }
      console.log("Error fetching subaccounts:", error);
      toast.error("Failed to fetch sub account data");
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchSubAccounts()
      .catch(() => {})
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Simpler & safer: we already have the row from DataGrid
  const handleEditClick = (row) => {
    if (!row?.sub_account_id) {
      console.error("Invalid row in handleEditClick:", row);
      toast.error("Unable to open record for edit");
      return;
    }

    navigate("/finance/subaccounts/add", {
      state: { subAccountId: row.sub_account_id },
    });
  };

  const handleDeleteClick = async (sub_account_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to change the status?"
    );
    if (!confirmDelete) return;

    const requestBody = {
      sub_account_id,
      action: "DELETE",
    };

    try {
      const response = await axios.post(
        baseUrl + "/subaccountmaster/",
        requestBody,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      toast.success("Record set to InActive");
      fetchSubAccounts();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to delete record");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "account_code",
        headerName: "Account Code",
        flex: 1,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "party_code",
        headerName: "Party Code",
        flex: 1,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "party_name",
        headerName: "Party Name",
        flex: 1.4,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "opening_balance",
        headerName: "Opening Balance",
        flex: 1.1,
        valueFormatter: (v) =>
          typeof v?.value === "number"
            ? v.value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : v.value,
        renderCell: (p) => (
          <MuiTooltip title={String(p.formattedValue ?? "")}>
            <span>{p.formattedValue}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "party_type",
        headerName: "Party Type",
        flex: 1,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "party_nature",
        headerName: "Party Nature",
        flex: 1,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "pan",
        headerName: "Pan",
        flex: 1,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "aadhar_no",
        headerName: "Aadhar No",
        flex: 1.2,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      {
        field: "is_active",
        headerName: "Status",
        flex: 0.8,
        renderCell: (params) => (
          <MuiTooltip title={params.value || ""}>
            <span>{params.value}</span>
          </MuiTooltip>
        ),
        sortable: true,
      },
      ...(canSubmit
        ? [
            {
              field: "actions",
              headerName: "Actions",
              flex: 1,
              sortable: false,
              renderCell: (params) => (
                <div className="tableActions">
                  <MuiTooltip title="Edit" arrow>
                    <span
                      className="commonActionIcons"
                      onClick={() => handleEditClick(params.row)} // 🔴 changed
                    >
                      <MdEdit />
                    </span>
                  </MuiTooltip>
                  {/* <MuiTooltip title="Delete" arrow>
                    <span
                      className="commonActionIcons"
                      onClick={() =>
                        handleDeleteClick(params.row.sub_account_id)
                      }
                    >
                      <MdDelete />
                    </span>
                  </MuiTooltip> */}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canSubmit]
  );

  const searchableColumns = [
    (r) => r.account_code,
    (r) => r.party_code,
    (r) => r.party_name,
    (r) => r.party_type,
    (r) => r.party_nature,
    (r) => r.pan,
    (r) => r.aadhar_no,
    (r) => r.is_active,
    (r) => r.opening_balance,
  ];

  const filteredRecords = (subAccounts || []).filter((row) =>
    searchableColumns.some((selector) =>
      String(selector(row) || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter state
  const [filter, setFilter] = useState({
    party_code: "",
    party_name: "",
    party_gst_registration_no: "",
    party_provisional_registration_no: "",
    aadhar_number: "",
    pan_number: "",
  });

  const handleFilterSearch = async () => {
    const payload = {
      _account_master_id: null,
      _party_code: filter.party_code || "",
      _party_name: filter.party_name || "",
      _active: null,
      _party_type_id: null,
      _party_nature_id: null,
      _party_gst_type_id: null,
      _party_state_id: null,
      _party_gst_registration_no: filter.party_gst_registration_no || "",
      _party_provisional_registration_no:
        filter.party_provisional_registration_no || "",
      _aadhar_number: filter.aadhar_number || "",
      _pan_number: filter.pan_number || "",
    };

    try {
      await fetchSubAccounts(payload);
      setShowFilterModal(false);
    } catch (error) {
      console.log("Full error object:", error);
      toast.error("Failed to fetch filtered data");
    }
  };

  const handleReset = async () => {
    setFilter({
      party_code: "",
      party_name: "",
      party_gst_registration_no: "",
      party_provisional_registration_no: "",
      aadhar_number: "",
      pan_number: "",
    });
    await fetchSubAccounts();
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Sub Account Master</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={
                <BootstrapTooltip id="tooltip-filter">Filter</BootstrapTooltip>
              }
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <BootstrapTooltip id="tooltip-add">Add</BootstrapTooltip>
                }
              >
                <Button
                  className="primaryBtn"
                  disabled={!canSubmit}
                  variant="primary"
                  onClick={() => navigate("/finance/subaccounts/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(r) => r.sub_account_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={600}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modalBodyScrollable">
          <Row>
            {/* Party Code */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="party_code">
                  <Form.Label>Party Code</Form.Label>
                  <Form.Control
                    type="text"
                    id="party_code"
                    value={filter.party_code}
                    placeholder="Enter Party Code"
                    maxLength={20}
                    onChange={(e) =>
                      setFilter({ ...filter, party_code: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            {/* Party Name */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="party_name">
                  <Form.Label>Party Name</Form.Label>
                  <Form.Control
                    type="text"
                    id="party_name"
                    value={filter.party_name}
                    placeholder="Enter Party Name"
                    maxLength={60}
                    onChange={(e) => {
                      const filteredValue = e.target.value.replace(
                        /[^a-zA-Z0-9 .]/g,
                        ""
                      );
                      setFilter({ ...filter, party_name: filteredValue });
                    }}
                  />
                </Form.Group>
              </div>
            </Col>

            {/* Aadhar No */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="aadhar_number">
                  <Form.Label>Aadhar No</Form.Label>
                  <Form.Control
                    type="text"
                    id="aadhar_number"
                    value={filter.aadhar_number}
                    placeholder="Enter Aadhar Number"
                    maxLength={12}
                    onChange={(e) =>
                      setFilter({ ...filter, aadhar_number: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            {/* PAN */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="pan_number">
                  <Form.Label>PAN</Form.Label>
                  <Form.Control
                    type="text"
                    id="pan_number"
                    value={filter.pan_number}
                    placeholder="Enter PAN"
                    maxLength={10}
                    onChange={(e) =>
                      setFilter({ ...filter, pan_number: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={handleReset}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              onClick={handleFilterSearch}
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubAccountMaster;
